import { useQuery } from '@tanstack/react-query';
import { getFetch } from '@legacy/utils/fetcher';
import { formatUrl } from '@legacy/utils/url';
import { parseMenu } from '@legacy/utils/menu';
import { IMenus, IMenusProperties } from '@legacy/apis/types/menusAPI';
import { useTranslation } from 'react-i18next';
import { useHasNewExpenseUI } from '@/hooks/featureFlags';

const SYSTEM_ADMINISTRATION_MENU_URL_PATH =
  '/api/v2/menus/system-administration';
export const SYSTEM_ADMINISTRATION_MENU_MSW_STATUS_KEY =
  'systemAdministrationMenuAPI';

export const SYSTEM_ADMINISTRATION_MENU_KEY = 'SYSTEM_ADMINISTRATION_MENU';

const getSystemAdministrationMenu = (): Promise<IMenus> =>
  getFetch({
    path: SYSTEM_ADMINISTRATION_MENU_URL_PATH,
    key: SYSTEM_ADMINISTRATION_MENU_MSW_STATUS_KEY,
  });

const getMyTimeLogAccountUrl = (systemAdminMenu: IMenusProperties[] = []) => {
  const myTimeLogAccountName = 'my timelog account';
  const { pageUrl = '' } =
    systemAdminMenu?.find(
      ({ name }) => name.toLowerCase() === myTimeLogAccountName,
    )?.subMenus?.[0] || {};
  return formatUrl(pageUrl);
};

export const getMyTimeLogAccountUrlForTest = getMyTimeLogAccountUrl;

const getDefaultSystemUrl = (
  systemAdminMenu: IMenusProperties[] = [],
): string => {
  const [firstElement] = systemAdminMenu;
  const { pageUrl = '' } = firstElement || {};
  return formatUrl(pageUrl);
};

export const getDefaultSystemUrlForTest = getDefaultSystemUrl;

export const useSystemAdministrationMenu = () => {
  const { data, ...restProps } = useQuery({
    queryKey: [SYSTEM_ADMINISTRATION_MENU_KEY],
    queryFn: getSystemAdministrationMenu,
  });
  const { t } = useTranslation('systemAdministrationNavigation');
  const parsedSystemAdminMenu = parseMenu(data, t);

  // #58467: to solve the confusing "currency settings" and "exchange rates" in system admin menu
  const hasExchangeRateSetting = parsedSystemAdminMenu.find((menu) =>
    menu?.subMenus?.some(
      (innerMenu) =>
        innerMenu.pageIdentifier ===
        'SystemAdministrationIntercompanyExchangeRateSetting',
    ),
  );
  // To exlude "Available currencies" page when new expense UI is enabled
  const hasNewExpenseUI = useHasNewExpenseUI();

  let systemAdminMenu = parsedSystemAdminMenu;

  if (hasExchangeRateSetting || hasNewExpenseUI) {
    systemAdminMenu = parsedSystemAdminMenu.map((menu) => {
      if (menu?.id !== 'SystemAdministrationInvoiceSetting') return menu;
      const newSubMenus = menu?.subMenus
        ?.map((subMenu) => {
          if (
            subMenu?.pageIdentifier !==
            'SystemAdministrationInvoiceCurrencySetting'
          )
            return subMenu;
          // when new expense UI is enabled, exclude "Available currencies" page completely
          if (hasNewExpenseUI) return;
          return {
            ...subMenu,
            translation: t('SystemAdministrationInvoiceAvailableCurrency'),
          };
        })
        .filter((subMenu) => !!subMenu); // filter out undefined items
      return {
        ...menu,
        subMenus: newSubMenus,
      };
    });
  }

  return {
    systemAdminMenu,
    defaultSystemUrl: getDefaultSystemUrl(systemAdminMenu),
    myTimeLogAccountUrl: getMyTimeLogAccountUrl(systemAdminMenu),
    isSystemAdministrationMenuEmpty: systemAdminMenu.length <= 0,
    ...restProps,
  };
};
